import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';

/**
 * Export element as PNG image
 * @param {HTMLElement} element - DOM element to capture
 * @param {string} filename - Output filename (without extension)
 * @returns {Promise<void>}
 */
export const exportToPNG = async (element, filename = 'export') => {
  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2, // Higher quality
      logging: false,
      useCORS: true
    });

    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();

    return true;
  } catch (error) {
    console.error('PNG export failed:', error);
    throw error;
  }
};

/**
 * Export table data as Excel file
 * @param {Object} tableData - Table data with headers and rows
 * @param {string} filename - Output filename (without extension)
 * @returns {void}
 */
export const exportToExcel = (tableData, filename = 'export') => {
  try {
    const { headers, rows } = tableData;

    // Create worksheet data (headers + rows)
    const wsData = [headers, ...rows];

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    const colWidths = headers.map((header, i) => {
      const maxLength = Math.max(
        header.length,
        ...rows.map((row) => (row[i] ? String(row[i]).length : 0))
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
    ws['!cols'] = colWidths;

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    // Save file
    XLSX.writeFile(wb, `${filename}.xlsx`);

    return true;
  } catch (error) {
    console.error('Excel export failed:', error);
    throw error;
  }
};

/**
 * Export table data as CSV
 * @param {Object} tableData - Table data with headers and rows
 * @param {string} filename - Output filename (without extension)
 * @returns {void}
 */
export const exportToCSV = (tableData, filename = 'export') => {
  try {
    const { headers, rows } = tableData;

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((cell) => {
            // Escape quotes and wrap in quotes if contains comma
            const str = String(cell);
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          })
          .join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob(['\uFEFF' + csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    link.click();

    return true;
  } catch (error) {
    console.error('CSV export failed:', error);
    throw error;
  }
};

/**
 * Remove emojis from text
 * @param {string} text - Text with emojis
 * @returns {string} - Text without emojis
 */
const removeEmojis = (text) => {
  // Comprehensive emoji removal regex
  // Covers all Unicode emoji ranges including extended pictographics
  return text
    .replace(/[\u{1F000}-\u{1FFFF}]/gu, '')  // All Supplemental Symbols (emoticons, pictographs, etc.)
    .replace(/[\u{2600}-\u{26FF}]/gu, '')    // Misc symbols (sun, moon, stars, etc.)
    .replace(/[\u{2700}-\u{27BF}]/gu, '')    // Dingbats
    .replace(/[\u{2300}-\u{23FF}]/gu, '')    // Misc Technical
    .replace(/[\u{2B50}-\u{2B55}]/gu, '')    // Stars and circles
    .replace(/[\u{FE00}-\u{FE0F}]/gu, '')    // Variation Selectors
    .replace(/[\u{200D}]/gu, '')             // Zero Width Joiner (used in composite emojis)
    .replace(/[\u{20E3}]/gu, '')             // Combining Enclosing Keycap
    .replace(/[\u{E0020}-\u{E007F}]/gu, '')  // Tags (used in flag sequences)
    .replace(/\s{2,}/g, ' ')                 // Multiple spaces to single
    .trim();
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @param {boolean} stripEmojis - Whether to remove emojis (default: true)
 * @returns {Promise<boolean>}
 */
export const copyToClipboard = async (text, stripEmojis = true) => {
  try {
    const cleanText = stripEmojis ? removeEmojis(text) : text;
    await navigator.clipboard.writeText(cleanText);
    return true;
  } catch (error) {
    // Fallback for older browsers
    const cleanText = stripEmojis ? removeEmojis(text) : text;
    const textarea = document.createElement('textarea');
    textarea.value = cleanText;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    return true;
  }
};

/**
 * Copy table as formatted text
 * @param {Object} tableData - Table data with headers and rows
 * @returns {Promise<boolean>}
 */
export const copyTableAsText = async (tableData) => {
  const { headers, rows } = tableData;

  const text = [
    headers.join('\t'),
    ...rows.map((row) => row.join('\t'))
  ].join('\n');

  return copyToClipboard(text);
};
